import { useEffect, useState } from "react";
import { ImagesGallery } from "../../components/Dialog/images-gallery.component";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../../../lib/supabase/supabaseClient";
import {
	ClientEntityType,
	JobEntityType,
	PatientEntityType,
	SupabaseTableEnum,
} from "../../../../lib/supabase/supabaseTypes";
import { Logger } from "../../../../lib/logger/Logger";
import {
	Chip,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Button,
	Tabs,
	Tab,
} from "@mui/material";
import { ChevronLeft } from "lucide-react";
import { attachFile } from "../../lib/utils";
import { useCentralStore } from "../../store/Central";
import { Loading } from "../../../../components/src/animations/loading";
import { useJobStore } from "../../store/Job";
import { getBorderColorTop } from "@/dentlab/src/pages/job-page/job-document/job-document.component";
import { JobDocumentTypeEnum } from "@/dentlab/src/pages/job-page/job-document/job-document.types";
import { FilesGallery } from "../../components/Dialog/files-gallery.component";

type loadingTypes = {
	upload: boolean,
	fetch: boolean,
	jobDocuments: boolean,
};

export const JobPage = () => {
	const { id, tab } = useParams();
	const [job, setJob] = useState<
		| (JobEntityType & {
				clients: ClientEntityType | null;
				patients: PatientEntityType | null;
		  })
		| null
	>(null);
	const { profile, membersLookup } = useCentralStore();
	const navigate = useNavigate();
	const {
		jobDocuments,
		fetchJobDocumentsWithItems,
		fetchJobFiles,
		createJobDocument,
	} = useJobStore();
	const [tabValue, setTabValue] = useState(tab ? Number(tab) : 0);
	const [loading, setLoading] = useState<loadingTypes>({
		upload: false,
		fetch: false,
		jobDocuments: false,
	});

	const fetchJob = async () => {
		if (!id) return;
		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.select(
				`*, ${SupabaseTableEnum.CLIENTS}(*), ${SupabaseTableEnum.PATIENTS}(*)`
			)
			.eq("id", Number(id))
			.single();

		if (error) {
			Logger.error(error, {}, "Error fetching Job");
			return;
		}

		setJob(data);
	};

	const handleImageUpload = async (image: File) => {
		setLoading({...loading, upload: true});
		setTabValue(1);
		if (!image || !profile || !job) return;
		await attachFile(image, profile, Number(id));
		await fetchJobFiles(Number(id));
		setLoading({...loading, upload: false});
	};

	const handleCreateDeliveryNote = async () => {
		setLoading({...loading, jobDocuments: true});
		await createJobDocument(
			Number(id),
			job?.clients?.id ?? "",
			JobDocumentTypeEnum.DeliveryNote
		);
		setLoading({...loading, jobDocuments: false});
	};

	const fetch = async () => {
		setLoading({...loading, fetch: true});
		if (id) {
			await fetchJob();
			await fetchJobDocumentsWithItems(Number(id));
			await fetchJobFiles(Number(id));
		}
		setLoading({...loading, fetch: false});
	};

	useEffect(() => {
		fetch();
	}, [id, fetchJobDocumentsWithItems, fetchJobFiles]);

	if (loading.fetch) {
		return <Loading />;
	}

	return (
		<div className="h-full flex flex-col overflow-y-auto">
			<div className="flex-shrink-0">
				<div className="flex items-center justify-between">
					<label
						onClick={() => navigate(-1)}
						className="text-blue-600 transition-colors active:text-blue-200 items-center flex"
					>
						<ChevronLeft />
						<span className="text-lg normal-case">Zurück</span>
					</label>
					<label className="cursor-pointer" htmlFor="jobImage">
						<div className="text-blue-600 transition-colors active:text-blue-200 items-center flex">
							<span className="text-lg normal-case">
								Bild hinzufügen
							</span>
						</div>
						<input
							id="jobImage"
							type="file"
							accept="image/*"
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (!file) return;
								handleImageUpload(file);
							}}
							style={{ display: "none" }}
						/>
					</label>
				</div>
				<div className="px-4 mt-4">
					<div className="flex flex-col gap-2 w-full">
						<div className="text-2xl font-bold">{job?.title}</div>
						<div className="font-bold text-gray-700">
							{job?.clients?.first_name ?? ""}
							{job?.clients?.first_name ? " " : ""}
							{job?.clients?.last_name ?? ""}
						</div>
						<div className="text-md text-gray-500">{job?.code}</div>
						{job?.patients && (
							<div className="mt-2" 
							onClick={() =>
								navigate(
									`/patient/${job?.patients?.id}`
								)
							}>
								<span className="text-md font-bold bg-gray-200 transition-colors active:bg-gray-100 p-2 rounded-md ">
								{`${
										job?.patients?.first_name ?? ""
									} ${job?.patients?.last_name ?? ""}`}
									</span>
							</div>
						)}
					</div>
				</div>
			</div>

			<Tabs
				value={tabValue}
				onChange={(_, newValue) => setTabValue(newValue)}
				variant="fullWidth"
				className="mt-4 border-b border-gray-200"
			>
				<Tab label="Dokumente" />
				<Tab label="Bilder" />
				<Tab label="Dateien" />
			</Tabs>

			<div className="flex-grow overflow-y-auto">
						<div role="tabpanel" hidden={tabValue !== 0}>
							{tabValue === 0 &&
								jobDocuments.length > 0 &&
								jobDocuments.map((jobDocument) => (
									<div
										key={jobDocument.id}
										className="mt-4 border-2 rounded-md"
										style={{
											borderColor: getBorderColorTop(
												true,
												jobDocument.type as JobDocumentTypeEnum
											),
										}}
									>
										<div className="text-2xl font-bold text-center flex flex-wrap justify-center">
											<span>{jobDocument.title}</span>
										</div>
										{jobDocument.job_items.length > 0 ? (
											<div className="overflow-x-auto w-full p-1">
												<Table padding="checkbox">
													<TableHead>
														<TableRow>
															<TableCell
																size="small"
																sx={{
																	fontWeight:
																		"bold",
																}}
															>
																Code
															</TableCell>
															<TableCell
																size="small"
																sx={{
																	fontWeight:
																		"bold",
																}}
															>
																Menge
															</TableCell>
															<TableCell
																size="small"
																sx={{
																	fontWeight:
																		"bold",
																}}
															>
																Lot
															</TableCell>
															<TableCell
																size="small"
																sx={{
																	fontWeight:
																		"bold",
																}}
															>
																Techniker
															</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{jobDocument.job_items.map(
															(jobItem) => (
																<TableRow
																	key={
																		jobItem.id
																	}
																>
																	<TableCell size="small">
																		{
																			jobItem.code
																		}
																	</TableCell>
																	<TableCell size="small">
																		{
																			jobItem.quantity
																		}
																	</TableCell>
																	<TableCell size="small">
																		{
																			jobItem.lot
																		}
																	</TableCell>
																	<TableCell size="small">
																		{membersLookup[
																			jobItem.profile_id ??
																				""
																		]
																			?.first_name ??
																			""}{" "}
																		{membersLookup[
																			jobItem.profile_id ??
																				""
																		]
																			?.last_name ??
																			""}
																	</TableCell>
																</TableRow>
															)
														)}
													</TableBody>
												</Table>
											</div>
										) : (
											<div className="text-center my-2">
												Keine Positionen vorhanden
											</div>
										)}
										<div className="text-center my-2">
											<Button
												variant="contained"
												sx={{
													backgroundColor: "#003261",
													color: "white",
													"&:hover": {
														backgroundColor:
															"#003261",
													},
												}}
												onClick={() =>
													navigate(
														`/scan/${id}/${jobDocument.id}`
													)
												}
											>
												Material einscannen
											</Button>
										</div>
									</div>
								))}

							{tabValue === 0 &&
								jobDocuments.length === 0 &&
								(job?.clients?.id !== null ? (
									<div className="text-center my-4">
										{loading.jobDocuments ? (
											<Loading />
										) : (
											<Button
												variant="contained"
											sx={{
												backgroundColor: "#003261",
												color: "white",
											}}
											onClick={handleCreateDeliveryNote}
										>
												Lieferschein erstellen
											</Button>
										)}
									</div>
								) : (
									<div className="text-center my-4">
										Keine Dokumente vorhanden
									</div>
								))}
						</div>

						<div role="tabpanel" hidden={tabValue !== 1}>
							{tabValue === 1 && (
								loading.upload ? (
									<div className="flex justify-center items-center h-full mt-8">
										<Loading />
									</div>
								) : (
									<ImagesGallery
										id={id as string}
										connectedOrganizationId={
										job?.clients?.connected_organization_id ??
										undefined
									}
								/>
								)
							)}
						</div>

						<div role="tabpanel" hidden={tabValue !== 2}>
							{tabValue === 2 && (
								<FilesGallery
									id={id as string}
									connectedOrganizationId={
										job?.clients?.connected_organization_id ??
										undefined
									}
								/>
							)}
						</div>
				</div>
		</div>
	);
};
